import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Row,
  Col,
  Input,
  InputNumber,
  Select,
  Button,
  Tag
} from 'antd';
import { toast } from '../../../utils/toast.js';
import {
  DownloadOutlined,
  CopyOutlined,
  PrinterOutlined,
  ThunderboltOutlined,
  ExclamationCircleOutlined,
  LockOutlined,
  ArrowLeftOutlined,
  PlusOutlined,
  DeleteOutlined,
  BulbOutlined,
} from '@ant-design/icons';
import {
  CAMPAIGN_LIB,
  CAMPAIGN_PROG,
  HOTRO,
  TUVAN,
  hoTroOf,
  mauState,
  trongState,
  tinhNhom,
  camOf,
  quaTag,
  goiY,
  chuanHoaBanNhap,
  lapKeHoach,
  downloadCampaignExcel,
  vanBan,
  num,
  vnd,
  tr,
  pct,
  dmy,
  dmyy
} from '../data/pharmacyCampaignData.js';
import { pharmacyToolApi } from '../../../api/pharmacy-tool.api.js';
import { savePendingLead, clearPendingLead, resendPendingLead } from '../../../utils/pendingLead.js';
import { useAuth } from '../../../contexts/AuthContext.jsx';
import { useToolAccess } from '../hooks/useToolAccess.js';
import { ToolPurchaseModal, ToolPreviewBanner } from '../components/ToolPurchase.jsx';
import { PROVINCES } from '../../../constants/vietnamAdministrativeDivisions.js';
import '../styles/pharmacy-campaign-tool.css';

const TOOL_SLUG = 'chuong-trinh-ban-hang';
const LOCAL_STORAGE_KEY_V2 = 'ctbh_draft_v2';
const LOCAL_STORAGE_KEY_OLD = 'ctbh_draft';
const LOCAL_STORAGE_UNLOCKED = 'ctbh_unlocked';
const LOCAL_STORAGE_STORE_NAME = 'ctbh_quay';
// Lead gửi lỗi (mạng / máy chủ) được giữ tạm ở đây và tự gửi lại khi mở lại công cụ (HT-06 D-01, YC-TOOL-09)
const LOCAL_STORAGE_PENDING_LEAD = 'ctbh_pending_lead';
// Danh mục 34 tỉnh / thành sau sáp nhập 2025, cùng định dạng với form Đăng ký, Hồ sơ (HT-06 F-13)
const PROVINCE_OPTIONS = PROVINCES.map((p) => ({ label: p.fullName, value: p.name }));

export function PharmacySalesCampaignToolPage() {
  const { user } = useAuth();
  const resultRef = useRef(null);

  // Mở công cụ: nếu còn lead gửi lỗi lần trước thì gửi lại (chạy ngầm, không báo khách)
  useEffect(() => {
    resendPendingLead(LOCAL_STORAGE_PENDING_LEAD, (p) => pharmacyToolApi.submitSalesCampaignLead(p));
  }, []);

  // Chưa mua công cụ: chế độ xem thử — chỉ xem số liệu mẫu và phương án mẫu,
  // sửa số liệu / mở khóa / tải Excel / sao chép / in thì mở modal mua
  const { access } = useToolAccess();
  const toolAccess = access?.[TOOL_SLUG];
  const isPreview = !!toolAccess && !toolAccess.hasAccess;
  const [buyModalOpen, setBuyModalOpen] = useState(false);
  const sampleInputs = useMemo(() => mauState(), []);

  // Khởi tạo state từ localStorage v2 hoặc migrate từ bản nháp cũ
  const [savedInputs, setSavedInputs] = useState(() => {
    try {
      const savedV2 = localStorage.getItem(LOCAL_STORAGE_KEY_V2);
      if (savedV2) {
        const draft = chuanHoaBanNhap(JSON.parse(savedV2));
        if (draft) return draft;
      }
      // Migrate từ bản draft cũ nếu có
      const savedOld = localStorage.getItem(LOCAL_STORAGE_KEY_OLD);
      if (savedOld) {
        const parsedOld = JSON.parse(savedOld);
        if (parsedOld && Array.isArray(parsedOld.nhom) && Array.isArray(parsedOld.qua)) {
          // Bản cũ để khai trương / tri ân trong ct: tách sang tongQuan
          if (parsedOld.tongQuan == null && Array.isArray(parsedOld.ct)) {
            parsedOld.tongQuan = '';
            ['khaitruong', 'trian'].forEach((id) => {
              const i = parsedOld.ct.indexOf(id);
              if (i >= 0) {
                parsedOld.ct.splice(i, 1);
                if (!parsedOld.tongQuan) parsedOld.tongQuan = id;
              }
            });
          }
          if (!parsedOld.pheu) parsedOld.pheu = mauState().pheu;
          const draft = chuanHoaBanNhap(parsedOld);
          if (draft) return draft;
        }
      }
    } catch (e) {
      console.warn('Lỗi đọc draft:', e);
    }
    return mauState();
  });
  const inputs = isPreview ? sampleInputs : savedInputs;
  const setInputs = (updater) => {
    if (isPreview) {
      setBuyModalOpen(true);
      return;
    }
    setSavedInputs(updater);
  };

  const [isSample, setIsSample] = useState(true);
  const [unlockedState, setUnlocked] = useState(() => {
    try {
      return localStorage.getItem(LOCAL_STORAGE_UNLOCKED) === '1' || user?.role === 'ADMIN';
    } catch (e) {
      return false;
    }
  });

  const [leadForm, setLeadForm] = useState(() => {
    let savedStoreName = '';
    try {
      savedStoreName = localStorage.getItem(LOCAL_STORAGE_STORE_NAME) || '';
    } catch (e) {
      // trình duyệt chặn localStorage: bỏ qua (SRS-IN-06e)
    }
    return {
      tenQuay: savedStoreName || user?.pharmacyName || '',
      tenChu: user?.fullName || '',
      dienThoai: user?.phone || '',
      tinh: user?.province || ''
    };
  });

  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const unlocked = unlockedState && !isPreview;

  // Tự động lưu nháp v2
  useEffect(() => {
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY_V2, JSON.stringify(savedInputs));
    } catch (e) {}
  }, [savedInputs]);

  // Động cơ tính toán trung tâm
  const planResult = useMemo(() => {
    return lapKeHoach(inputs);
  }, [inputs]);

  // Gợi ý chương trình hợp quầy
  const recPrograms = useMemo(() => {
    return goiY(inputs);
  }, [inputs]);

  // Tổng hợp tỷ trọng & biên lãi
  const nhomSummary = useMemo(() => {
    return tinhNhom(inputs.nhom || []);
  }, [inputs.nhom]);

  // Cập nhật giá trị trường cơ bản
  const handleFieldChange = (field, val) => {
    setIsSample(false);
    setInputs((prev) => ({ ...prev, [field]: val }));
  };

  // Cập nhật giá trị trường trong pheu
  const handlePheuChange = (field, val) => {
    setIsSample(false);
    setInputs((prev) => {
      const nextPheu = {
        ...(prev.pheu || {}),
        [field]: val
      };
      let nextTongQuan = prev.tongQuan;
      if (field === 'doSK' && val === 'co' && !prev.tongQuan) {
        nextTongQuan = 'ngayhoi';
        toast.success('Đã tự động chọn "Ngày hội sức khỏe" (Tầng 1) để kích hoạt phễu đo sức khỏe & tư vấn TPCN!');
      }
      return {
        ...prev,
        tongQuan: nextTongQuan,
        pheu: nextPheu
      };
    });
  };

  // Chọn chip (đơn chọn hoặc đa chọn)
  const handleChipClick = (key, valId, isMulti) => {
    setIsSample(false);
    setInputs((prev) => {
      if (!isMulti) {
        return { ...prev, [key]: valId };
      }
      const list = Array.isArray(prev[key]) ? [...prev[key]] : [];
      const idx = list.indexOf(valId);
      if (idx >= 0) {
        list.splice(idx, 1);
      } else {
        if (key === 'kenh' && valId === 'chuaco') {
          return { ...prev, [key]: ['chuaco'] };
        }
        if (key === 'kenh' && valId !== 'chuaco') {
          const noIdx = list.indexOf('chuaco');
          if (noIdx >= 0) list.splice(noIdx, 1);
        }
        list.push(valId);
      }
      return { ...prev, [key]: list };
    });
  };

  // Toggle chip nguồn hỗ trợ (hoTro)
  const handleToggleHoTro = (id) => {
    setIsSample(false);
    setInputs((prev) => {
      const list = Array.isArray(prev.hoTro) ? [...prev.hoTro] : [];
      const idx = list.indexOf(id);
      if (idx >= 0) {
        list.splice(idx, 1);
      } else {
        list.push(id);
      }
      return { ...prev, hoTro: list };
    });
  };

  // Chọn chương trình tầng 1 (tổng quan - tối đa 1, bấm lại để bỏ chọn)
  const handleSelectTongQuan = (id) => {
    setIsSample(false);
    setInputs((prev) => ({
      ...prev,
      tongQuan: prev.tongQuan === id ? '' : id
    }));
  };

  // Chọn / bỏ chọn chương trình tầng 2 (chương trình nhỏ)
  const handleToggleProg = (id) => {
    setIsSample(false);
    setInputs((prev) => {
      const current = Array.isArray(prev.ct) ? [...prev.ct] : [];
      const idx = current.indexOf(id);
      if (idx >= 0) {
        current.splice(idx, 1);
      } else {
        current.push(id);
      }
      return { ...prev, ct: current };
    });
  };

  // Cập nhật bảng nhóm hàng
  const handleNhomChange = (index, field, value) => {
    setIsSample(false);
    setInputs((prev) => {
      const nextNhom = [...prev.nhom];
      nextNhom[index] = { ...nextNhom[index], [field]: value };
      return { ...prev, nhom: nextNhom };
    });
  };

  // Thêm dòng quà
  const handleAddQua = () => {
    setIsSample(false);
    setInputs((prev) => ({
      ...prev,
      qua: [
        ...(prev.qua || []),
        { ten: '', loai: 'thuong', von: '', le: '', sl: '', hsd: '' }
      ]
    }));
  };

  // Xóa dòng quà
  const handleRemoveQua = (index) => {
    setIsSample(false);
    setInputs((prev) => {
      const nextQua = [...prev.qua];
      nextQua.splice(index, 1);
      return { ...prev, qua: nextQua };
    });
  };

  // Cập nhật dòng quà
  const handleQuaChange = (index, field, value) => {
    setIsSample(false);
    setInputs((prev) => {
      const nextQua = [...prev.qua];
      nextQua[index] = { ...nextQua[index], [field]: value };
      return { ...prev, qua: nextQua };
    });
  };

  // Cuộn tới kết quả
  const handleScrollToResult = () => {
    if (resultRef.current) {
      resultRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Nạp dữ liệu mẫu
  const handleLoadSample = () => {
    const s = mauState();
    setSavedInputs(s);
    setIsSample(true);
    toast.info('Đã nạp lại dữ liệu mẫu quầy thị trấn (bản 2.2)');
  };

  // Nhập lại từ đầu
  const handleReset = () => {
    const s = trongState();
    setInputs(s);
    setIsSample(false);
    toast.info('Đã xóa dữ liệu mẫu. Hãy nhập số liệu quầy của bạn');
  };

  // Gửi Lead & Mở khóa
  const handleLeadSubmit = async () => {
    if (isPreview) {
      setBuyModalOpen(true);
      return;
    }
    const { tenQuay, tenChu, dienThoai, tinh } = leadForm;
    // Cùng quy tắc với máy chủ: đổi 84 thành 0, thêm 0 khi thiếu, di động 10 số đầu 03/05/07/08/09.
    let cleanPhone = String(dienThoai || '').replace(/\D/g, '');
    if (cleanPhone.length === 11 && cleanPhone.startsWith('84')) cleanPhone = '0' + cleanPhone.slice(2);
    else if (cleanPhone.length === 9) cleanPhone = '0' + cleanPhone;

    if (!tenQuay || tenQuay.trim().length < 2 || !/^0[35789]\d{8}$/.test(cleanPhone)) {
      toast.warning('Vui lòng điền tên quầy và số di động 10 số (đầu 03, 05, 07, 08 hoặc 09)');
      return;
    }

    setLeadSubmitting(true);
    try {
      const maPhanLoai = inputs.tongQuan === 'khaitruong'
        ? 'mo_moi'
        : inputs.tongQuan === 'ngayhoi'
        ? 'ngay_hoi'
        : inputs.tongQuan === 'trian'
        ? 'tri_an'
        : (inputs.ct || []).includes('combo') || (inputs.ct || []).includes('moc') || (inputs.ct || []).includes('muanhieu')
        ? 'upsale'
        : 'giu_chan';

      // Mọi số tiền trong lead đều theo TRIỆU ĐỒNG (HT-06 D-06). Công cụ tính theo đồng, ô nhập đơn giá theo nghìn đồng.
      const trieu = (dong) => Math.round((Number(dong) || 0) / 1e3) / 1e3; // đồng -> triệu, giữ 3 chữ số thập phân
      const payload = {
        tenQuay: tenQuay.trim(),
        tenChu: tenChu.trim(),
        dienThoai: cleanPhone,
        tinh: tinh.trim(),
        khuVuc: inputs.khuVuc,
        viTri: inputs.viTri,
        loaiHinh: inputs.loaiHinh,
        tepKhach: inputs.tep,
        tongQuan: inputs.tongQuan || null,
        chuongTrinh: inputs.ct,
        hoTro: inputs.hoTro,
        doSucKhoe: inputs.pheu?.doSK === 'co',
        tuVan: inputs.pheu?.tuVan,
        mauDungThu: num(inputs.pheu?.mau) || 0,
        luotDo: planResult?.ev?.pheu?.luotDo || 0,
        donTPCN: planResult?.ev?.pheu?.donTPCN || 0,
        dsTang: trieu(planResult?.dsTang),
        laiGop90: trieu(planResult?.lai3),
        doanhSoNgay: num(inputs.dsNgay) || 0, // ô nhập đã là triệu đồng
        donGiaTB: trieu((num(inputs.donGia) || 0) * 1000), // ô nhập là nghìn đồng
        bienTB: nhomSummary.bienTB * 100,
        // Ngân sách công cụ thực dùng: số khách nhập, hoặc số tự đề xuất khi khách để trống
        nganSach: trieu(planResult?.ns),
        chiPhi: trieu(planResult?.chiPhi),
        batDau: inputs.batDau,
        soNgay: inputs.soNgay,
        canMuaQua: (planResult?.plans || []).some((p) => (p.qua || []).some((q) => q.nguon === 'mua')),
        canHangDongHanh: !!inputs.tongQuan && !(inputs.hoTro || []).includes('hang_ban'),
        maPhanLoai
      };

      try {
        await pharmacyToolApi.submitSalesCampaignLead(payload);
        clearPendingLead(LOCAL_STORAGE_PENDING_LEAD);
      } catch (err) {
        // Dữ liệu sai (400) thì báo để người dùng sửa; lỗi mạng / máy chủ / quá số lần gửi vẫn mở khóa.
        if (err?.statusCode === 400) {
          toast.error(err.message || 'Thông tin chưa hợp lệ, vui lòng kiểm tra lại');
          return;
        }
        // Giữ lead trên trình duyệt để tự gửi lại lần sau, không để mất lead (HT-06 D-01)
        console.warn('Gửi lead qua API lỗi, giữ lại để gửi sau và tiếp tục mở khóa:', err);
        savePendingLead(LOCAL_STORAGE_PENDING_LEAD, payload);
      }

      setUnlocked(true);
      try {
        localStorage.setItem(LOCAL_STORAGE_UNLOCKED, '1');
        localStorage.setItem(LOCAL_STORAGE_STORE_NAME, tenQuay.trim());
      } catch (e) {
        // không ghi được localStorage: vẫn mở khóa trong phiên này (SRS-IN-06e)
      }
      toast.success('Mở khóa kế hoạch triển khai đầy đủ thành công!');
    } catch (error) {
      toast.error('Có lỗi xảy ra khi lưu thông tin. Vui lòng thử lại');
    } finally {
      setLeadSubmitting(false);
    }
  };

  // Cổng lead: Excel, sao chép, in chỉ dùng được sau khi mở khóa (HT-06 S-15)
  const requireUnlocked = () => {
    if (isPreview) {
      setBuyModalOpen(true);
      return false;
    }
    if (unlocked) return true;
    toast.info('Vui lòng điền thông tin cơ sở để mở khóa trước khi tải, sao chép hoặc in phương án.');
    try {
      document.querySelector('.campaign-gate-card')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } catch (e) { }
    return false;
  };

  // Xuất file Excel 10 sheet
  const handleExportExcel = () => {
    if (!requireUnlocked()) return;
    if (!planResult) {
      toast.warning('Chưa có phương án tính toán để xuất Excel');
      return;
    }
    try {
      const ok = downloadCampaignExcel(planResult, leadForm.tenQuay);
      if (ok) {
        toast.success('Đã tải file Excel kế hoạch 10 sheet có công thức sống');
      } else {
        toast.error('Không thể tạo file Excel lúc này');
      }
    } catch (e) {
      console.error('Lỗi xuất Excel:', e);
      toast.error('Lỗi khi xuất file Excel');
    }
  };

  // Sao chép toàn bộ phương án
  const handleCopyText = () => {
    if (!requireUnlocked()) return;
    if (!planResult) return;
    try {
      const text = vanBan(planResult, leadForm.tenQuay);
      navigator.clipboard.writeText(text);
      toast.success('Đã sao chép toàn bộ phương án chi tiết vào bộ nhớ tạm');
    } catch (e) {
      toast.error('Không thể sao chép văn bản');
    }
  };

  // Sao chép tin nhắn mẫu của giải pháp giảm chi phí
  const handleCopyMessage = (msg) => {
    if (!requireUnlocked()) return;
    if (!msg) return;
    try {
      navigator.clipboard.writeText(msg);
      toast.success('Đã sao chép mẫu tin nhắn');
    } catch (e) {
      toast.error('Không thể sao chép tin nhắn');
    }
  };

  // In phương án
  const handlePrint = () => {
    if (!requireUnlocked()) return;
    window.print();
  };

  // Các nhóm chương trình tầng 2
  const tang2Groups = useMemo(() => {
    const groups = [];
    CAMPAIGN_PROG.forEach((p) => {
      if (p.tang === 2 && !groups.includes(p.nhom)) {
        groups.push(p.nhom);
      }
    });
    return groups;
  }, []);

  return (
    <div className="campaign-tool-container">
      {/* Hero Banner Header */}
      <div className="campaign-hero-banner">
        <div className="campaign-hero-inner">
          {/* Nút quay về cùng kiểu các trang công cụ khác */}
          <div style={{ marginBottom: 14 }}>
            <Link
              to="/tools"
              style={{
                color: '#d1fae5',
                fontSize: 13,
                fontWeight: 600,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                textDecoration: 'none',
                background: 'rgba(255, 255, 255, 0.12)',
                padding: '4px 14px',
                borderRadius: 20,
                border: '1px solid rgba(255, 255, 255, 0.2)',
                transition: 'all 0.2s ease',
              }}
            >
              <ArrowLeftOutlined /> Trung Tâm Công Cụ
            </Link>
          </div>
          <Row gutter={[28, 24]} align="middle" justify="space-between">
            <Col xs={24} lg={14}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                <Tag color="#10b981" style={{ fontWeight: 700, borderRadius: 4, padding: '2px 8px' }}>
                  CHƯƠNG TRÌNH BÁN HÀNG x2 DOANH SỐ
                </Tag>
                <span style={{ fontSize: 13, color: '#a7f3d0' }}>
                  Bản 2.2 · Phễu 2 tầng &amp; Excel 10 sheet sống
                </span>
              </div>
              <h1 className="campaign-hero-title">
                Cẩm nang xây chương trình bán hàng nhà thuốc <em>x2 doanh số</em>
              </h1>
              <p className="campaign-hero-desc">
                Nhập vài con số về quầy, khai hàng tồn có thể làm quà, chọn chương trình tổng quan và chương trình nhỏ. Tool trả về phương án triển khai chi tiết: phễu đo sức khỏe → tư vấn → đơn TPCN, mốc quà, ngân sách, cách xin hãng và phường hỗ trợ để giảm chi phí, câu nói cho nhân viên, tin nhắn mẫu, lịch chạy và file Excel theo dõi.
              </p>

              <div className="campaign-principles-card">
                <b>Ba nguyên tắc tool luôn giữ vững:</b>
                <ul>
                  <li>Không khuyến mại thuốc kê đơn và sữa cho trẻ dưới 24 tháng.</li>
                  <li>Không lấy thuốc làm quà tặng hay dùng thử.</li>
                  <li>Chi phí quà tính theo giá vốn thật, không tính theo giá bán lẻ.</li>
                </ul>
              </div>
            </Col>

            {/* Hình ảnh minh họa công cụ */}
            <Col xs={24} lg={10}>
              <div
                style={{
                  position: 'relative',
                  borderRadius: 14,
                  overflow: 'hidden',
                  boxShadow: '0 12px 28px rgba(0, 0, 0, 0.28)',
                  border: '2px solid rgba(255, 255, 255, 0.22)',
                  aspectRatio: '16/9',
                  background: 'rgba(0, 0, 0, 0.15)',
                }}
              >
                <img
                  src="/images/tools/tool-chuong-trinh-ban-hang-hero.jpg"
                  alt="Cẩm nang xây chương trình bán hàng nhà thuốc x2 doanh số"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
              </div>
            </Col>
          </Row>
        </div>
      </div>

      {/* Main Input Form */}
      <div className="campaign-main-wrap">
        {isPreview && (
          <ToolPreviewBanner onBuy={() => setBuyModalOpen(true)}>
            <b>Bạn đang xem thử với số liệu mẫu quầy thị trấn.</b> Kéo xuống để xem phương án mẫu tool trả về. Mua công
            cụ để nhập số liệu quầy của bạn, mở toàn bộ kế hoạch triển khai và tải file Excel 10 sheet.
          </ToolPreviewBanner>
        )}
        {/* STEP 1: Quầy & Khách Hàng */}
        <section className="campaign-step-card">
          <div className="campaign-step-header">
            <span className="campaign-step-num">1</span>
            <div>
              <h2 className="campaign-step-title">Quầy thuốc và khách hàng</h2>
              <p className="campaign-step-desc">
                Khai báo bối cảnh điểm bán để thuật toán gợi ý chương trình sát thực tế và tính đúng hệ số lượt khách.
              </p>
            </div>
          </div>

          <Row gutter={[20, 16]}>
            <Col xs={24} sm={12} md={8}>
              <span className="campaign-field-label">Khu vực quầy</span>
              <div className="campaign-chips">
                {CAMPAIGN_LIB.khuVuc.map((kv) => (
                  <button
                    key={kv.id}
                    type="button"
                    className={`campaign-chip ${inputs.khuVuc === kv.id ? 'active' : ''}`}
                    onClick={() => handleChipClick('khuVuc', kv.id, false)}
                  >
                    {kv.ten}
                  </button>
                ))}
              </div>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <span className="campaign-field-label">Vị trí điểm bán</span>
              <div className="campaign-chips">
                {CAMPAIGN_LIB.viTri.map((vt) => (
                  <button
                    key={vt.id}
                    type="button"
                    className={`campaign-chip ${inputs.viTri === vt.id ? 'active' : ''}`}
                    onClick={() => handleChipClick('viTri', vt.id, false)}
                  >
                    {vt.ten}
                  </button>
                ))}
              </div>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <span className="campaign-field-label">Loại hình cơ sở</span>
              <div className="campaign-chips">
                {CAMPAIGN_LIB.loaiHinh.map((lh) => (
                  <button
                    key={lh.id}
                    type="button"
                    className={`campaign-chip ${inputs.loaiHinh === lh.id ? 'active' : ''}`}
                    onClick={() => handleChipClick('loaiHinh', lh.id, false)}
                  >
                    {lh.ten}
                  </button>
                ))}
              </div>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <span className="campaign-field-label">Doanh số trung bình / ngày</span>
              <InputNumber
                style={{ width: '100%' }}
                min={0.5}
                max={500}
                step={0.5}
                addonAfter="triệu đ"
                value={inputs.dsNgay}
                onChange={(val) => handleFieldChange('dsNgay', val)}
              />
            </Col>

            <Col xs={24} sm={12} md={6}>
              <span className="campaign-field-label">Giá trị trung bình 1 hóa đơn</span>
              <InputNumber
                style={{ width: '100%' }}
                min={10}
                max={5000}
                step={5}
                addonAfter="nghìn đ"
                value={inputs.donGia}
                onChange={(val) => handleFieldChange('donGia', val)}
              />
            </Col>

            <Col xs={24} sm={12} md={6}>
              <span className="campaign-field-label">Số ngày chạy chương trình</span>
              <InputNumber
                style={{ width: '100%' }}
                min={1}
                max={60}
                step={1}
                addonAfter="ngày"
                value={inputs.soNgay}
                onChange={(val) => handleFieldChange('soNgay', val)}
              />
            </Col>

            <Col xs={24} sm={12} md={6}>
              <span className="campaign-field-label">Ngày bắt đầu</span>
              <Input
                type="date"
                value={inputs.batDau}
                onChange={(e) => handleFieldChange('batDau', e.target.value)}
              />
            </Col>

            <Col xs={24} md={12}>
              <span className="campaign-field-label">Tệp khách chính (chọn nhiều)</span>
              <div className="campaign-chips">
                {CAMPAIGN_LIB.tep.map((t) => {
                  const active = (inputs.tep || []).includes(t.id);
                  return (
                    <button
                      key={t.id}
                      type="button"
                      className={`campaign-chip ${active ? 'active' : ''}`}
                      onClick={() => handleChipClick('tep', t.id, true)}
                    >
                      {t.ten}
                    </button>
                  );
                })}
              </div>
            </Col>

            <Col xs={24} md={12}>
              <span className="campaign-field-label">Kênh kết nối khách hàng</span>
              <div className="campaign-chips">
                {CAMPAIGN_LIB.kenh.map((k) => {
                  const active = (inputs.kenh || []).includes(k.id);
                  return (
                    <button
                      key={k.id}
                      type="button"
                      className={`campaign-chip ${active ? 'active' : ''}`}
                      onClick={() => handleChipClick('kenh', k.id, true)}
                    >
                      {k.ten}
                    </button>
                  );
                })}
              </div>
            </Col>
          </Row>
        </section>

        {/* STEP 2: Nhóm Hàng & Biên Lãi */}
        <section className="campaign-step-card">
          <div className="campaign-step-header">
            <span className="campaign-step-num">2</span>
            <div>
              <h2 className="campaign-step-title">Nhóm hàng và biên lãi</h2>
              <p className="campaign-step-desc">
                Tỷ trọng doanh thu và biên lãi gộp từng nhóm. Thuốc kê đơn và sữa trẻ dưới 24 tháng bị pháp luật cấm khuyến mại nên tool tự động khóa, không tính vào mốc quà, tích điểm hay combo.
              </p>
            </div>
          </div>

          <div className="campaign-table-wrap">
            <table className="campaign-table">
              <thead>
                <tr>
                  <th style={{ minWidth: 200 }}>Tên nhóm hàng</th>
                  <th className="r" style={{ width: 140 }}>Tỷ trọng (%)</th>
                  <th className="r" style={{ width: 140 }}>Biên lãi gộp (%)</th>
                  <th style={{ width: 220 }}>Áp dụng khuyến mại</th>
                </tr>
              </thead>
              <tbody>
                {(inputs.nhom || []).map((nh, idx) => {
                  const cm = camOf(nh);
                  return (
                    <tr key={idx}>
                      <td>
                        <Input
                          size="small"
                          value={nh.ten}
                          onChange={(e) => handleNhomChange(idx, 'ten', e.target.value)}
                        />
                      </td>
                      <td className="r">
                        <InputNumber
                          size="small"
                          min={0}
                          max={100}
                          step={1}
                          value={nh.tt}
                          onChange={(val) => handleNhomChange(idx, 'tt', val)}
                          addonAfter="%"
                        />
                      </td>
                      <td className="r">
                        <InputNumber
                          size="small"
                          min={0}
                          max={100}
                          step={1}
                          value={nh.bien}
                          onChange={(val) => handleNhomChange(idx, 'bien', val)}
                          addonAfter="%"
                        />
                      </td>
                      <td>
                        {cm === 'rx' ? (
                          <span className="campaign-tag bad">Khóa tự động: Thuốc kê đơn</span>
                        ) : cm === 'sua' ? (
                          <span className="campaign-tag bad">Khóa tự động: Sữa dưới 24 tháng</span>
                        ) : (
                          <span className="campaign-tag ok">Được áp dụng KM</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: 12, fontSize: 13.5, color: '#475569' }}>
            Biên lãi gộp bình quân cả quầy: <b>{pct(nhomSummary.bienTB)}</b> · Nhóm được khuyến mại chiếm{' '}
            <b>{pct(nhomSummary.tyKM)}</b> doanh thu, biên <b>{pct(nhomSummary.bienKM)}</b>
            {Math.abs(nhomSummary.tong - 100) > 0.5 && nhomSummary.tong > 0 && (
              <span className="campaign-tag warn" style={{ marginLeft: 8 }}>
                Tổng tỷ trọng đang là {pct(nhomSummary.tong / 100, 0)}, tool tự quy về 100%
              </span>
            )}
          </div>
        </section>

        {/* STEP 3: Hàng Tồn Làm Quà Hoặc Cần Xả */}
        <section className="campaign-step-card">
          <div className="campaign-step-header">
            <span className="campaign-step-num">3</span>
            <div>
              <h2 className="campaign-step-title">Hàng tồn có thể làm quà hoặc cần xả</h2>
              <p className="campaign-step-desc">
                Quà hãng tặng tính chi phí 0đ. Hàng chậm bán và cận hạn tính chi phí thấp hơn giá vốn vì dòng tiền đang nằm chết. Hàng còn dưới 1 tháng hạn bị loại bỏ hoàn toàn.
              </p>
            </div>
          </div>

          <div className="campaign-table-wrap">
            <table className="campaign-table">
              <thead>
                <tr>
                  <th style={{ minWidth: 180 }}>Tên mặt hàng</th>
                  <th style={{ width: 160 }}>Nguồn hàng</th>
                  <th className="r" style={{ width: 120 }}>Giá vốn (đ)</th>
                  <th className="r" style={{ width: 120 }}>Giá bán lẻ (đ)</th>
                  <th className="r" style={{ width: 90 }}>Số lượng</th>
                  <th className="r" style={{ width: 110 }}>Hạn còn (tháng)</th>
                  <th style={{ width: 170 }}>Mục đích dùng</th>
                  <th style={{ width: 44 }}></th>
                </tr>
              </thead>
              <tbody>
                {(inputs.qua || []).map((q, idx) => (
                  <tr key={idx}>
                    <td>
                      <Input
                        size="small"
                        placeholder="Nhập tên hàng"
                        value={q.ten}
                        onChange={(e) => handleQuaChange(idx, 'ten', e.target.value)}
                      />
                    </td>
                    <td>
                      <Select
                        size="small"
                        style={{ width: '100%' }}
                        value={q.loai}
                        onChange={(val) => handleQuaChange(idx, 'loai', val)}
                        options={CAMPAIGN_LIB.loaiQua.map((l) => ({ label: l.ten, value: l.id }))}
                      />
                    </td>
                    <td className="r">
                      <InputNumber
                        size="small"
                        min={0}
                        step={1000}
                        value={q.von}
                        onChange={(val) => handleQuaChange(idx, 'von', val)}
                      />
                    </td>
                    <td className="r">
                      <InputNumber
                        size="small"
                        min={0}
                        step={1000}
                        value={q.le}
                        onChange={(val) => handleQuaChange(idx, 'le', val)}
                      />
                    </td>
                    <td className="r">
                      <InputNumber
                        size="small"
                        min={0}
                        step={1}
                        value={q.sl}
                        onChange={(val) => handleQuaChange(idx, 'sl', val)}
                      />
                    </td>
                    <td className="r">
                      <InputNumber
                        size="small"
                        min={0}
                        step={1}
                        placeholder="0 = ko hạn"
                        value={q.hsd}
                        onChange={(val) => handleQuaChange(idx, 'hsd', val)}
                      />
                    </td>
                    <td>
                      <span dangerouslySetInnerHTML={{ __html: quaTag(q) }} />
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        size="small"
                        onClick={() => handleRemoveQua(idx)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: 12 }}>
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={handleAddQua}
              style={{ color: '#005537', borderColor: '#005537' }}
            >
              Thêm một dòng hàng quà / hàng xả
            </Button>
          </div>
        </section>

        {/* STEP 4: Chọn Chương Trình (2 Tầng) */}
        <section className="campaign-step-card">
          <div className="campaign-step-header">
            <span className="campaign-step-num">4</span>
            <div>
              <h2 className="campaign-step-title">Chọn chương trình (2 tầng)</h2>
              <p className="campaign-step-desc">
                <b>Chương trình tổng quan</b> tạo sóng kéo khách vào quầy (chọn tối đa 1 hoặc bỏ qua). <b>Chương trình nhỏ</b> chạy bên trong để biến lượt khách thành đơn to hơn, đặc biệt là đơn TPCN (nên chọn 2–3). Nhãn <span className="campaign-tag ok" style={{ fontSize: 11 }}>Hợp quầy</span> là gợi ý theo dữ liệu bạn vừa nhập.
              </p>
            </div>
          </div>

          {/* Tầng 1: Chương trình tổng quan */}
          <div className="campaign-pgroup-title" style={{ marginTop: 6 }}>
            Tầng 1 · Chương trình tổng quan — chọn tối đa 1 (bấm lại để bỏ chọn)
          </div>
          <div className="campaign-pcards" style={{ marginBottom: 20 }}>
            {CAMPAIGN_PROG.filter((p) => p.tang === 1).map((p) => {
              const selected = inputs.tongQuan === p.id;
              const rec = recPrograms[p.id];
              return (
                <div
                  key={p.id}
                  className={`campaign-pcard radio ${selected ? 'selected' : ''}`}
                  onClick={() => handleSelectTongQuan(p.id)}
                >
                  <b className="campaign-pcard-title">{p.ten}</b>
                  <span className="campaign-pcard-desc">{p.mo}</span>
                  {rec && <span className="campaign-pcard-rec">Hợp quầy: {rec}</span>}
                </div>
              );
            })}
          </div>

          {/* Tầng 2: Các chương trình nhỏ */}
          <div className="campaign-pgroups">
            {tang2Groups.map((groupName) => (
              <div key={groupName}>
                <div className="campaign-pgroup-title">Tầng 2 · {groupName}</div>
                <div className="campaign-pcards">
                  {CAMPAIGN_PROG.filter((p) => p.tang === 2 && p.nhom === groupName).map((p) => {
                    const selected = (inputs.ct || []).includes(p.id);
                    const rec = recPrograms[p.id];
                    return (
                      <div
                        key={p.id}
                        className={`campaign-pcard ${selected ? 'selected' : ''}`}
                        onClick={() => handleToggleProg(p.id)}
                      >
                        <b className="campaign-pcard-title">{p.ten}</b>
                        <span className="campaign-pcard-desc">{p.mo}</span>
                        {rec && <span className="campaign-pcard-rec">Hợp quầy: {rec}</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <Row gutter={[20, 16]} style={{ marginTop: 20 }}>
            <Col xs={24} sm={12}>
              <span className="campaign-field-label">Mục tiêu tăng doanh số trong kỳ</span>
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                max={300}
                step={5}
                addonAfter="%"
                value={inputs.mucTieu}
                onChange={(val) => handleFieldChange('mucTieu', val)}
              />
            </Col>
            <Col xs={24} sm={12}>
              <span className="campaign-field-label">Ngân sách tối đa (để trống = tool tự đề xuất)</span>
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                step={0.5}
                placeholder="Tool tự đề xuất ngân sách tối ưu"
                addonAfter="triệu đ"
                value={inputs.nganSach}
                onChange={(val) => handleFieldChange('nganSach', val)}
              />
            </Col>
          </Row>
        </section>

        {/* STEP 5: Tư Vấn, Upsale & Nguồn Hỗ Trợ Thực Hiện */}
        <section className="campaign-step-card">
          <div className="campaign-step-header">
            <span className="campaign-step-num">5</span>
            <div>
              <h2 className="campaign-step-title">Tư vấn, upsale và nguồn hỗ trợ thực hiện</h2>
              <p className="campaign-step-desc">
                Đo sức khỏe, tư vấn đúng nhu cầu và dùng thử là cách chuyển khách thành đơn TPCN. Tích những nguồn hỗ trợ bạn đã có hoặc sẽ xin — tool trừ thẳng vào chi phí và gợi ý thêm cách giảm chi phí ở phần kết quả.
              </p>
            </div>
          </div>

          <Row gutter={[20, 16]}>
            <Col xs={24} sm={12} md={6}>
              <span className="campaign-field-label">Đo sức khỏe miễn phí</span>
              <div className="campaign-chips">
                {[
                  { id: 'co', ten: 'Có đo' },
                  { id: 'khong', ten: 'Không đo' }
                ].map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    className={`campaign-chip ${inputs.pheu?.doSK === o.id ? 'active' : ''}`}
                    onClick={() => handlePheuChange('doSK', o.id)}
                  >
                    {o.ten}
                  </button>
                ))}
              </div>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <span className="campaign-field-label">Số người trực {inputs.pheu?.doSK === 'co' ? 'đo, tư vấn' : 'tư vấn'}</span>
              <InputNumber
                style={{ width: '100%' }}
                min={1}
                max={6}
                step={1}
                disabled={inputs.pheu?.doSK !== 'co'}
                addonAfter="người"
                value={inputs.pheu?.nguoiDo}
                onChange={(val) => handlePheuChange('nguoiDo', val)}
              />
            </Col>

            <Col xs={24} sm={12} md={6}>
              <span className="campaign-field-label">Số ngày cao điểm sự kiện</span>
              <InputNumber
                style={{ width: '100%' }}
                min={1}
                max={10}
                step={1}
                disabled={!inputs.tongQuan}
                addonAfter="ngày"
                value={inputs.pheu?.ngaySK}
                onChange={(val) => handlePheuChange('ngaySK', val)}
              />
            </Col>

            <Col xs={24} sm={12} md={6}>
              <span className="campaign-field-label">{inputs.pheu?.doSK === 'co' ? 'Ai tư vấn sau khi đo' : 'Ai phụ trách bàn tư vấn'}</span>
              <div className="campaign-chips">
                {TUVAN.map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    className={`campaign-chip ${inputs.pheu?.tuVan === o.id ? 'active' : ''}`}
                    onClick={() => handlePheuChange('tuVan', o.id)}
                  >
                    {o.ten}
                  </button>
                ))}
              </div>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <span className="campaign-field-label">Số mẫu dùng thử (TPCN, mỹ phẩm)</span>
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                step={10}
                addonAfter="mẫu"
                value={inputs.pheu?.mau}
                onChange={(val) => handlePheuChange('mau', val)}
              />
            </Col>

            <Col xs={24} sm={12} md={8}>
              <span className="campaign-field-label">Giá vốn 1 mẫu nếu tự bỏ tiền</span>
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                step={1}
                addonAfter="nghìn đ"
                value={inputs.pheu?.giaMau}
                onChange={(val) => handlePheuChange('giaMau', val)}
              />
            </Col>

            <Col xs={24} sm={12} md={8}>
              <span className="campaign-field-label">Giá trị trung bình 1 đơn TPCN</span>
              <InputNumber
                style={{ width: '100%' }}
                min={50}
                step={10}
                addonAfter="nghìn đ"
                value={inputs.pheu?.giaTPCN}
                onChange={(val) => handlePheuChange('giaTPCN', val)}
              />
            </Col>

            <Col xs={24}>
              <span className="campaign-field-label">Nguồn hỗ trợ thực hiện — đã có hoặc sẽ xin (chọn nhiều)</span>
              <div className="campaign-chips">
                {HOTRO.map((h) => {
                  const active = (inputs.hoTro || []).includes(h.id);
                  return (
                    <button
                      key={h.id}
                      type="button"
                      title={h.ten}
                      className={`campaign-chip ${active ? 'active' : ''}`}
                      onClick={() => handleToggleHoTro(h.id)}
                    >
                      {h.ngan}
                    </button>
                  );
                })}
              </div>
            </Col>
          </Row>

          {/* Hộp xem nhanh tác động trực tiếp của Phễu sự kiện & Đo sức khỏe */}
          <div className="campaign-funnel-impact-box">
            <div className="campaign-impact-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <ThunderboltOutlined style={{ color: '#005537', fontSize: 16 }} />
                <b style={{ color: '#005537', fontSize: 14.5 }}>Tác động trực tiếp của phễu sự kiện</b>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {inputs.pheu?.doSK === 'co' ? (
                  <Tag color="success">Đang bật đo sức khỏe</Tag>
                ) : (
                  <Tag color="default">Không đo (chỉ tư vấn & dùng thử)</Tag>
                )}
                {inputs.tongQuan ? (
                  <Tag color="blue">{CAMPAIGN_PROG.find((p) => p.id === inputs.tongQuan)?.ten || inputs.tongQuan}</Tag>
                ) : (
                  <Tag color="warning">Chưa kích hoạt sự kiện Tầng 1</Tag>
                )}
              </div>
            </div>

            {inputs.tongQuan && planResult?.ev?.pheu ? (
              <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
                <Col xs={12} sm={6}>
                  <div className="campaign-impact-card">
                    <span className="campaign-impact-label">Lượt khách cao điểm</span>
                    <b className="campaign-impact-val">{Math.round(planResult.ev.pheu.visits).toLocaleString('vi-VN')} lượt</b>
                    <span className="campaign-impact-sub">
                      {planResult.ctx.evDays} ngày cao điểm (h={planResult.ctx.h.toFixed(2).replace('.', ',')})
                    </span>
                  </div>
                </Col>
                <Col xs={12} sm={6}>
                  <div className="campaign-impact-card">
                    <span className="campaign-impact-label">Lượt đo sức khỏe</span>
                    <b className="campaign-impact-val" style={{ color: planResult.ev.pheu.doOn ? '#005537' : '#94a3b8' }}>
                      {planResult.ev.pheu.doOn ? `${planResult.ev.pheu.luotDo} lượt` : '0 (không đo)'}
                    </b>
                    <span className="campaign-impact-sub">
                      {planResult.ev.pheu.doOn
                        ? `Công suất tối đa: ${planResult.ev.pheu.cap} lượt`
                        : 'Chuyển toàn bộ thành bàn tư vấn'}
                    </span>
                  </div>
                </Col>
                <Col xs={12} sm={6}>
                  <div className="campaign-impact-card">
                    <span className="campaign-impact-label">Đơn TPCN tại chỗ</span>
                    <b className="campaign-impact-val">
                      {planResult.ev.pheu.donTPCN} đơn
                    </b>
                    <span className="campaign-impact-sub">
                      + {tr(planResult.ev.pheu.dsTPCN)} doanh số TPCN
                    </span>
                  </div>
                </Col>
                <Col xs={12} sm={6}>
                  <div className="campaign-impact-card">
                    <span className="campaign-impact-label">Chi phí sự kiện</span>
                    <b className="campaign-impact-val" style={{ color: '#c2410c' }}>
                      {vnd(planResult.ev.chiPhi)}
                    </b>
                    <span className="campaign-impact-sub">
                      {planResult.ev.chiPhiGoc > planResult.ev.chiPhi
                        ? `Tiết kiệm ${vnd(planResult.ev.chiPhiGoc - planResult.ev.chiPhi)} nhờ hỗ trợ`
                        : 'Chưa có nguồn hỗ trợ'}
                    </span>
                  </div>
                </Col>
              </Row>
            ) : (
              <div
                style={{
                  marginTop: 10,
                  padding: '10px 14px',
                  background: '#fffbeb',
                  borderRadius: 8,
                  border: '1px solid #fde68a',
                  fontSize: 13,
                  color: '#92400e'
                }}
              >
                💡 <b>Chưa kích hoạt phễu sự kiện:</b> Bấm chọn <b>"Có đo"</b> ở trên (tool sẽ tự động bật{' '}
                <i>Ngày hội sức khỏe</i>), hoặc lên <b>Bước 4</b> chọn một chương trình Tầng 1 (Khai trương / Tri ân / Ngày hội) để kích hoạt phễu đo, tính chi phí và doanh số TPCN.
              </div>
            )}
          </div>

          {/* Action Trigger Buttons */}
          <div className="campaign-action-bar">
            <Button
              type="primary"
              size="large"
              icon={<ThunderboltOutlined />}
              onClick={handleScrollToResult}
              style={{ background: '#005537', borderColor: '#005537', fontWeight: 700, borderRadius: 10 }}
            >
              Lên chương trình ngay
            </Button>

            <Button
              type="default"
              size="large"
              onClick={handleLoadSample}
              style={{ color: '#005537', borderColor: '#005537', borderRadius: 10 }}
            >
              Nạp dữ liệu mẫu
            </Button>

            <Button
              type="text"
              size="large"
              onClick={handleReset}
              style={{ color: '#64748b' }}
            >
              Nhập lại từ đầu
            </Button>

            <span style={{ fontSize: 13, color: '#64748b', marginLeft: 'auto' }}>
              {isPreview || isSample ? 'Đang hiển thị số liệu mẫu quầy thị trấn' : 'Đã cập nhật theo số liệu quầy của bạn'}
            </span>
          </div>
        </section>

        {/* RESULTS CONTAINER */}
        <div ref={resultRef} className="campaign-result-section">
          {planResult ? (
            <>
              {/* Header Overview */}
              <div className="campaign-result-header">
                <div>
                  <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: '#0f172a' }}>
                    {planResult.ev ? planResult.ev.ten : 'Chương trình bán hàng'}{' '}
                    {planResult.plans.filter((p) => p.tang === 2).length > 0
                      ? `+ ${planResult.plans.filter((p) => p.tang === 2).length} chương trình nhỏ`
                      : ''}
                  </h2>
                  <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 14 }}>
                    Thời gian: {dmyy(planResult.ctx.from)} – {dmyy(planResult.ctx.to)} ({planResult.ctx.T} ngày)
                  </p>
                </div>

                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <Button
                    icon={<CopyOutlined />}
                    onClick={handleCopyText}
                    style={{ borderRadius: 8 }}
                  >
                    Sao chép toàn bộ
                  </Button>
                  <Button
                    icon={<PrinterOutlined />}
                    onClick={handlePrint}
                    style={{ borderRadius: 8 }}
                  >
                    In phương án
                  </Button>
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={handleExportExcel}
                    style={{ background: '#005537', borderColor: '#005537', fontWeight: 700, borderRadius: 8 }}
                  >
                    Xuất Excel 10 Sheet
                  </Button>
                </div>
              </div>

              {/* 4 KPI Cards */}
              <div className="campaign-kpi-grid">
                <div className="campaign-kpi-item">
                  <span className="campaign-kpi-label">Ngân sách tối đa</span>
                  <div className="campaign-kpi-val">{tr(planResult.ns)}</div>
                  <span className="campaign-kpi-sub">
                    {planResult.nsNhap > 0 ? 'Mức trần tự đặt' : 'Mức an toàn tool đề xuất'}
                  </span>
                </div>

                <div className="campaign-kpi-item">
                  <span className="campaign-kpi-label">Chi phí sau hỗ trợ</span>
                  <div className="campaign-kpi-val">
                    {planResult.chiPhiGoc > planResult.chiPhi && (
                      <span className="campaign-strike">{tr(planResult.chiPhiGoc)}</span>
                    )}
                    {tr(planResult.chiPhi)}
                  </div>
                  <span className="campaign-kpi-sub">
                    {planResult.chiPhiGoc > planResult.chiPhi
                      ? `Đã giảm ${tr(planResult.chiPhiGoc - planResult.chiPhi)} nhờ hỗ trợ`
                      : 'Chi phí quà tặng & giảm giá'}
                  </span>
                </div>

                <div className="campaign-kpi-item">
                  <span className="campaign-kpi-label">Doanh số tăng trong kỳ</span>
                  <div className="campaign-kpi-val" style={{ color: '#047857' }}>
                    +{tr(planResult.dsTang)}
                  </div>
                  <span className="campaign-kpi-sub">
                    Tăng +{pct(planResult.dsTang / planResult.ctx.DSky, 0)} so với kỳ thường
                  </span>
                </div>

                <div className="campaign-kpi-item">
                  <span className="campaign-kpi-label">Lãi gộp sau 90 ngày</span>
                  <div className="campaign-kpi-val" style={{ color: '#005537' }}>
                    +{tr(planResult.lai3)}
                  </div>
                  <span className="campaign-kpi-sub">
                    1đ chi phí → {planResult.hs3.toFixed(2).replace('.', ',')}đ lãi gộp
                  </span>
                </div>
              </div>

              {/* Khối Hiệu quả theo 3 mốc */}
              <div className="campaign-hz">
                <div className="campaign-hz-item">
                  <span className="campaign-field-label" style={{ color: '#005537', marginBottom: 2 }}>
                    Trong kỳ ({planResult.ctx.T} ngày)
                  </span>
                  <span className="campaign-hz-val">+{tr(planResult.dsTang)}</span>
                  <span className="campaign-hz-sub">
                    Lãi gộp +{tr(planResult.lai)} · 1đ CP → {planResult.hs.toFixed(2).replace('.', ',')}đ lãi
                  </span>
                </div>

                <div className="campaign-hz-item">
                  <span className="campaign-field-label" style={{ color: '#005537', marginBottom: 2 }}>
                    30 ngày sau
                  </span>
                  <span className="campaign-hz-val">+{tr(planResult.ds30)}</span>
                  <span className="campaign-hz-sub">
                    Lãi gộp +{tr(planResult.lai30)} · mua sau dùng thử &amp; voucher
                  </span>
                </div>

                <div className="campaign-hz-item">
                  <span className="campaign-field-label" style={{ color: '#005537', marginBottom: 2 }}>
                    90 ngày sau
                  </span>
                  <span className="campaign-hz-val">+{tr(planResult.ds90)}</span>
                  <span className="campaign-hz-sub">
                    Lãi gộp +{tr(planResult.lai90)} · khách mới quay lại &amp; liệu trình TPCN
                  </span>
                </div>
              </div>

              {/* Phễu Sự Kiện Trực Quan (nếu có sự kiện) */}
              {planResult.ev && planResult.ev.pheu && (
                <div className="campaign-funnel">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <b style={{ fontSize: 15, color: '#005537' }}>
                      Phễu sự kiện {planResult.ev.ten} ({planResult.ctx.evDays} ngày cao điểm)
                    </b>
                    <span style={{ fontSize: 13, color: '#64748b' }}>
                      Hệ số khách h = {planResult.ctx.h.toFixed(2).replace('.', ',')}
                    </span>
                  </div>

                  <div className="campaign-frow">
                    <span>Lượt khách vào quầy</span>
                    <div className="campaign-fbar">
                      <div className="campaign-fbar-fill" style={{ width: '100%' }}></div>
                    </div>
                    <b>{Math.round(planResult.ev.pheu.visits).toLocaleString('vi-VN')} khách</b>
                  </div>

                  {planResult.ev.pheu.doOn && (
                    <div className="campaign-frow">
                      <span>Lượt đo sức khỏe</span>
                      <div className="campaign-fbar">
                        <div
                          className="campaign-fbar-fill"
                          style={{
                            width: `${Math.min(100, Math.round((planResult.ev.pheu.luotDo / Math.max(planResult.ev.pheu.visits, 1)) * 100))}%`
                          }}
                        ></div>
                      </div>
                      <b>
                        {planResult.ev.pheu.luotDo} lượt{' '}
                        <small style={{ color: '#64748b', fontWeight: 400 }}>
                          (max {planResult.ev.pheu.cap})
                        </small>
                      </b>
                    </div>
                  )}

                  {planResult.ev.pheu.doOn ? (
                    <div className="campaign-frow">
                      <span>Đơn TPCN sau tư vấn đo</span>
                      <div className="campaign-fbar">
                        <div
                          className="campaign-fbar-fill"
                          style={{
                            width: `${Math.min(100, Math.round((planResult.ev.pheu.donTPCN / Math.max(planResult.ev.pheu.luotDo, 1)) * 100))}%`
                          }}
                        ></div>
                      </div>
                      <b>
                        {planResult.ev.pheu.donTPCN} đơn{' '}
                        <small style={{ color: '#005537' }}>
                          ({pct(planResult.ev.pheu.cr, 0)})
                        </small>
                      </b>
                    </div>
                  ) : (
                    <div className="campaign-frow">
                      <span>Khách mua TPCN tại chỗ (tư vấn trực tiếp)</span>
                      <div className="campaign-fbar">
                        <div
                          className="campaign-fbar-fill"
                          style={{
                            width: `${Math.min(100, Math.round((planResult.ev.pheu.donTPCN / Math.max(planResult.ev.pheu.visits, 1)) * 100))}%`
                          }}
                        ></div>
                      </div>
                      <b>
                        {planResult.ev.pheu.donTPCN} đơn{' '}
                        <small style={{ color: '#005537' }}>
                          ({pct(planResult.ev.pheu.donTPCN / Math.max(planResult.ev.pheu.visits, 1), 1)} khách)
                        </small>
                      </b>
                    </div>
                  )}

                  {planResult.ev.pheu.mauN > 0 && (
                    <div className="campaign-frow">
                      <span>Mẫu thử phát ra</span>
                      <div className="campaign-fbar">
                        <div
                          className="campaign-fbar-fill"
                          style={{
                            width: `${Math.min(100, Math.round((planResult.ev.pheu.dungMau / Math.max(planResult.ev.pheu.mauN, 1)) * 100))}%`
                          }}
                        ></div>
                      </div>
                      <b>
                        {planResult.ev.pheu.mauN} mẫu → {planResult.ev.pheu.dungMau} mua lại
                      </b>
                    </div>
                  )}

                  <div className="campaign-frow">
                    <span>Khách mới giữ lại (90 ngày)</span>
                    <div className="campaign-fbar">
                      <div
                        className="campaign-fbar-fill"
                        style={{
                          width: `${Math.min(100, Math.round((planResult.ev.pheu.khMoi * 0.4 / Math.max(planResult.ev.pheu.visits, 1)) * 100))}%`
                        }}
                      ></div>
                    </div>
                    <b>{Math.round(planResult.ev.pheu.khMoi * 0.4)} khách</b>
                  </div>
                </div>
              )}

              {/* Alerts & Warnings */}
              {planResult.canhBao && planResult.canhBao.length > 0 && (
                <div className="campaign-alerts-box">
                  {planResult.canhBao.map((al, idx) => (
                    <div key={idx} className={`campaign-alert-row ${al[0]}`}>
                      {al[0] === 'bad' ? (
                        <ExclamationCircleOutlined style={{ fontSize: 18, marginTop: 2 }} />
                      ) : (
                        <BulbOutlined style={{ fontSize: 18, marginTop: 2 }} />
                      )}
                      <div>
                        <b>{al[1]}: </b>
                        <span>{al[2]}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* GATE LOCK FOR DETAILED EXECUTION PLAN */}
              {!unlocked ? (
                <div className="campaign-gate-card">
                  <div style={{ textAlign: 'center', maxWidth: 640, margin: '0 auto' }}>
                    <div style={{ fontSize: 36, color: '#005537', marginBottom: 8 }}>
                      <LockOutlined />
                    </div>
                    <h3 style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', margin: '0 0 8px' }}>
                      Mở khóa toàn bộ kế hoạch triển khai &amp; tải file Excel 10 Sheet
                    </h3>
                    <p style={{ color: '#64748b', fontSize: 14, margin: '0 0 20px' }}>
                      Để xem chi tiết kịch bản nói chuyện nhân viên, nội dung poster, tin nhắn Zalo, 11 giải pháp giảm chi phí kèm mẫu thư ngỏ và tải bộ file Excel 10 sheet công thức sống, vui lòng xác nhận thông tin cơ sở của bạn.
                    </p>

                    <Row gutter={[14, 14]}>
                      <Col xs={24} sm={12}>
                        <Input
                          placeholder="Tên nhà thuốc / quầy thuốc *"
                          value={leadForm.tenQuay}
                          onChange={(e) => setLeadForm({ ...leadForm, tenQuay: e.target.value })}
                        />
                      </Col>
                      <Col xs={24} sm={12}>
                        <Input
                          placeholder="Họ tên người đại diện"
                          value={leadForm.tenChu}
                          onChange={(e) => setLeadForm({ ...leadForm, tenChu: e.target.value })}
                        />
                      </Col>
                      <Col xs={24} sm={12}>
                        <Input
                          placeholder="Số điện thoại / Zalo nhận kế hoạch *"
                          value={leadForm.dienThoai}
                          onChange={(e) => setLeadForm({ ...leadForm, dienThoai: e.target.value })}
                        />
                      </Col>
                      <Col xs={24} sm={12}>
                        <Select
                          placeholder="Tỉnh / Thành phố"
                          value={leadForm.tinh || undefined}
                          onChange={(val) => setLeadForm({ ...leadForm, tinh: val || '' })}
                          options={PROVINCE_OPTIONS}
                          showSearch
                          optionFilterProp="label"
                          allowClear
                          style={{ width: '100%' }}
                        />
                      </Col>
                      <Col xs={24}>
                        <Button
                          type="primary"
                          size="large"
                          block
                          loading={leadSubmitting}
                          onClick={handleLeadSubmit}
                          style={{
                            background: '#005537',
                            borderColor: '#005537',
                            fontWeight: 700,
                            borderRadius: 10,
                            height: 46
                          }}
                        >
                          {isPreview ? 'Mua công cụ để mở khóa kế hoạch' : <>Mở khóa kế hoạch &amp; Tải file Excel</>}
                        </Button>
                      </Col>
                    </Row>
                  </div>
                </div>
              ) : null}

              {/* DETAIL PROGRAM CARDS (Mờ nếu chưa unlock) */}
              <div className={!unlocked ? 'campaign-locked-blur' : ''}>
                {planResult.plans.map((p) => (
                  <div key={p.code} className="campaign-sheet-card">
                    <div className="campaign-sheet-header">
                      <span className="campaign-code-tag">{p.code}</span>
                      <div>
                        <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: '#0f172a' }}>
                          {p.ten} {p.tang === 1 && <span className="campaign-tag gift" style={{ fontSize: 11 }}>Chương trình tổng quan</span>}
                        </h3>
                        <p style={{ margin: '2px 0 0', color: '#64748b', fontSize: 13 }}>
                          Thời gian: {p.thoiGian} · Đòn bẩy: <b>{p.donbay}</b>
                        </p>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <span style={{ fontSize: 12, color: '#64748b' }}>Chi phí sau hỗ trợ</span>
                        <div style={{ fontSize: 18, fontWeight: 800, color: '#005537' }}>
                          {p.chiPhiGoc > p.chiPhi && (
                            <span className="campaign-strike">{tr(p.chiPhiGoc)}</span>
                          )}
                          {tr(p.chiPhi)}
                        </div>
                      </div>
                    </div>

                    <div className="campaign-sheet-body">
                      <p style={{ fontSize: 14, color: '#334155', marginBottom: p.doiTuong ? 6 : 16 }}>
                        <b>Mục tiêu:</b> {p.muc}
                      </p>
                      {p.doiTuong && (
                        <p style={{ fontSize: 14, color: '#334155', marginBottom: 16 }}>
                          <b>Đối tượng:</b> {p.doiTuong}
                        </p>
                      )}

                      {/* Bảng cơ chế */}
                      {p.bang && (
                        <div style={{ marginBottom: 18 }}>
                          <div className="campaign-sec-title">Cơ chế ưu đãi</div>
                          <div className="campaign-table-wrap">
                            <table className="campaign-table">
                              <thead>
                                <tr>
                                  {p.bang.head.map((h, i) => (
                                    <th key={i}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {p.bang.rows.map((r, ri) => (
                                  <tr key={ri}>
                                    {r.map((c, ci) => (
                                      <td key={ci} dangerouslySetInnerHTML={{ __html: String(c) }} />
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Chi phí chi tiết trước và sau hỗ trợ */}
                      {p.cpLines && p.cpLines.length > 0 && (
                        <div style={{ marginBottom: 18 }}>
                          <div className="campaign-sec-title">Chi phí chi tiết &amp; Nguồn hỗ trợ</div>
                          <div className="campaign-table-wrap">
                            <table className="campaign-table">
                              <thead>
                                <tr>
                                  <th>Khoản chi</th>
                                  <th className="r" style={{ width: 140 }}>Chi phí gốc</th>
                                  <th className="r" style={{ width: 140 }}>Mức giảm</th>
                                  <th className="r" style={{ width: 150 }}>Chi phí sau hỗ trợ</th>
                                </tr>
                              </thead>
                              <tbody>
                                {p.cpLines.map((l, li) => {
                                  const g = (inputs.hoTro || []).reduce((acc, hId) => {
                                    const h = hoTroOf(hId);
                                    return acc + (h?.giam?.[l.nhom] || 0);
                                  }, 0);
                                  const rate = Math.min(0.9, g);
                                  const costAfter = l.tien * (1 - rate);
                                  return (
                                    <tr key={li}>
                                      <td>{l.ten}</td>
                                      <td className="r">{vnd(l.tien)}</td>
                                      <td className="r">
                                        {rate > 0 ? (
                                          <span className="campaign-tag ok">-{pct(rate, 0)}</span>
                                        ) : (
                                          <span style={{ color: '#94a3b8' }}>0%</span>
                                        )}
                                      </td>
                                      <td className="r">
                                        <b>{vnd(costAfter)}</b>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Bảng gợi ý sau khi đo (BANG_TU_VAN) */}
                      {p.bangTuVan && (
                        <div style={{ marginBottom: 18 }}>
                          <div className="campaign-sec-title" style={{ color: '#047857' }}>
                            Bảng gợi ý chuyên môn sau khi đo sức khỏe (Dược sĩ duyệt trước khi dùng)
                          </div>
                          <div className="campaign-table-wrap">
                            <table className="campaign-table">
                              <thead>
                                <tr>
                                  {p.bangTuVan.head.map((h, i) => (
                                    <th key={i}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {p.bangTuVan.rows.map((r, ri) => (
                                  <tr key={ri}>
                                    <td style={{ fontWeight: 600, color: '#0f172a' }}>{r[0]}</td>
                                    <td>{r[1]}</td>
                                    <td>
                                      <span className="campaign-tag gift">{r[2]}</span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Kịch bản nói chuyện & tư vấn */}
                      {p.kichBan && p.kichBan.length > 0 && (
                        <div style={{ marginBottom: 18 }}>
                          <div className="campaign-sec-title">Kịch bản câu nói cho nhân viên / dược sĩ</div>
                          {p.kichBan.map((kb, kbi) => (
                            <div key={kbi} className="campaign-quote-box">
                              <b style={{ color: '#005537' }}>{kb.tinh}:</b> "{kb.noi}"
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Poster & Zalo */}
                      <Row gutter={[16, 16]} style={{ marginBottom: 18 }}>
                        {p.poster && (
                          <Col xs={24} md={12}>
                            <div className="campaign-sec-title">Tiêu đề Poster &amp; Băng rôn</div>
                            <div className="campaign-poster-box">
                              <b style={{ fontSize: 16, color: '#005537', display: 'block' }}>
                                {p.poster.hl}
                              </b>
                              <span style={{ fontSize: 13, color: '#475569' }}>{p.poster.sub}</span>
                            </div>
                          </Col>
                        )}
                        {p.zalo && (
                          <Col xs={24} md={12}>
                            <div className="campaign-sec-title">Mẫu tin nhắn Zalo gửi khách</div>
                            <div className="campaign-zalo-box">{p.zalo}</div>
                            <Button
                              size="small"
                              icon={<CopyOutlined />}
                              onClick={() => handleCopyMessage(p.zalo)}
                              style={{ marginTop: 8, color: '#005537', borderColor: '#005537' }}
                            >
                              Sao chép tin nhắn Zalo
                            </Button>
                          </Col>
                        )}
                      </Row>

                      {/* KPI & Chuẩn bị */}
                      <Row gutter={[20, 16]}>
                        <Col xs={24} sm={12}>
                          <div className="campaign-sec-title">Chỉ số KPI cần theo dõi</div>
                          <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13.5, color: '#334155' }}>
                            {p.kpi.map((kp, kpiIdx) => (
                              <li key={kpiIdx} style={{ margin: '4px 0' }}>
                                {kp[0]}: <b>{kp[1]}</b>
                              </li>
                            ))}
                          </ul>
                        </Col>
                        <Col xs={24} sm={12}>
                          <div className="campaign-sec-title">Các bước chuẩn bị</div>
                          <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13.5, color: '#334155' }}>
                            {p.chuanBi.map((cb, cbi) => (
                              <li key={cbi} style={{ margin: '4px 0' }}>
                                {cb}
                              </li>
                            ))}
                          </ul>
                        </Col>
                      </Row>

                      {/* Thể lệ & điều kiện pháp lý (gồm DK_SU_KIEN khi có sự kiện) — SRS công cụ 4.4 khối 7, SRS-BR-06 */}
                      {p.dieuKien && p.dieuKien.length > 0 && (
                        <div className="campaign-legal-box">
                          <div className="campaign-sec-title">Thể lệ &amp; điều kiện pháp lý</div>
                          <ul>
                            {p.dieuKien.map((dk, dki) => (
                              <li key={dki}>{dk}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {/* 11 GIẢI PHÁP GIẢM CHI PHÍ (V2.2) */}
                <div className="campaign-sheet-card">
                  <div className="campaign-sheet-header">
                    <span className="campaign-code-tag" style={{ background: '#fef3c7', color: '#92400e', borderColor: '#f59e0b' }}>
                      TIẾT KIỆM
                    </span>
                    <div>
                      <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: '#0f172a' }}>
                        11 Giải pháp xin hỗ trợ &amp; giảm chi phí
                      </h3>
                      <p style={{ margin: '2px 0 0', color: '#64748b', fontSize: 13 }}>
                        Trừ thẳng vào từng khoản chi, kèm các bước thực hiện và mẫu tin nhắn / đề nghị gửi đối tác
                      </p>
                    </div>
                  </div>

                  <div className="campaign-sheet-body">
                    {planResult.giamChiPhi
                      .filter((x) => x.on || x.lienQuan)
                      .map((sol) => {
                        const h = sol.h;
                        // Replace dynamic tags in template message
                        let msg = h.mau || '';
                        if (msg) {
                          const tenSK = planResult.ev ? planResult.ev.ten : 'Chương trình chăm sóc sức khỏe';
                          const visits = planResult.ev?.pheu?.visits ? Math.round(planResult.ev.pheu.visits) : 300;
                          const luotDo = planResult.ev?.pheu?.luotDo || 100;
                          const mau = inputs.pheu?.mau || 100;
                          const tep = (inputs.tep || []).map((t) => {
                            const found = CAMPAIGN_LIB.tep.find((x) => x.id === t);
                            return found ? found.ten : t;
                          }).join(', ') || 'người cao tuổi, người có bệnh mãn tính';
                          const slQua = (planResult.plans || []).reduce((acc, p) => acc + (p.qua || []).reduce((a2, q) => a2 + q.sl, 0), 0) || 50;

                          msg = msg
                            .replace(/\{TEN_SK\}/g, tenSK)
                            .replace(/\{LUOT\}/g, visits)
                            .replace(/\{DO\}/g, luotDo)
                            .replace(/\{MAU\}/g, mau)
                            .replace(/\{TEP\}/g, tep)
                            .replace(/\{SLQUA\}/g, slQua);
                        }

                        return (
                          <div key={h.id} className={`campaign-save-card ${sol.on ? 'on' : ''}`}>
                            <div className="campaign-save-header">
                              <div>
                                <b>{sol.on ? '[Đã chọn] ' : ''}{h.ten}</b>
                                {sol.tietKiem > 0 && (
                                  <Tag color={sol.on ? 'green' : 'gold'} style={{ marginLeft: 8, fontWeight: 700 }}>
                                    Tiết kiệm ~{vnd(sol.tietKiem)}
                                  </Tag>
                                )}
                              </div>
                              <span style={{ fontSize: 13, color: '#64748b' }}>
                                Điều kiện: {h.dk}
                              </span>
                            </div>

                            <ol>
                              {h.buoc.map((b, bi) => (
                                <li key={bi}>{b}</li>
                              ))}
                            </ol>

                            {msg && (
                              <div className="campaign-save-tpl">
                                <details>
                                  <summary>Xem mẫu tin nhắn / văn bản đề nghị</summary>
                                  <div className="campaign-save-tpl-content">{msg}</div>
                                  <div style={{ marginTop: 8 }}>
                                    <Button
                                      size="small"
                                      icon={<CopyOutlined />}
                                      onClick={() => handleCopyMessage(msg)}
                                      style={{ color: '#005537', borderColor: '#005537' }}
                                    >
                                      Sao chép tin nhắn này
                                    </Button>
                                  </div>
                                </details>
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                </div>

                {/* MA TRẬN CỘNG DỒN */}
                {planResult.matran && planResult.matran.length > 0 && (
                  <div className="campaign-sheet-card">
                    <div className="campaign-sheet-header">
                      <span className="campaign-code-tag">QUY TẮC</span>
                      <div>
                        <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: '#0f172a' }}>
                          Ma trận cộng dồn và loại trừ
                        </h3>
                        <p style={{ margin: '2px 0 0', color: '#64748b', fontSize: 13 }}>
                          Quy tắc giải quyết xung đột khi khách thỏa mãn nhiều ưu đãi cùng lúc
                        </p>
                      </div>
                    </div>
                    <div className="campaign-sheet-body">
                      <div className="campaign-table-wrap">
                        <table className="campaign-table">
                          <thead>
                            <tr>
                              <th>Chương trình A</th>
                              <th>Chương trình B</th>
                              <th>Quy tắc giải quyết</th>
                            </tr>
                          </thead>
                          <tbody>
                            {planResult.matran.map((m, mi) => (
                              <tr key={mi}>
                                <td style={{ fontWeight: 600 }}>{m[0]}</td>
                                <td style={{ fontWeight: 600 }}>{m[1]}</td>
                                <td>{m[2]}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                {/* LỊCH TRIỂN KHAI */}
                {planResult.lich && planResult.lich.length > 0 && (
                  <div className="campaign-sheet-card">
                    <div className="campaign-sheet-header">
                      <span className="campaign-code-tag">TIẾN ĐỘ</span>
                      <div>
                        <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: '#0f172a' }}>
                          Lịch triển khai theo ngày
                        </h3>
                        <p style={{ margin: '2px 0 0', color: '#64748b', fontSize: 13 }}>
                          Mốc thời gian chuẩn bị, đào tạo, xin hỗ trợ, chạy và nghiệm thu
                        </p>
                      </div>
                    </div>
                    <div className="campaign-sheet-body">
                      {planResult.lich.map((l, li) => (
                        <div key={li} className="campaign-timeline-row">
                          <div className="campaign-timeline-date">{dmy(l[0])}</div>
                          <div>
                            <b style={{ color: '#0f172a' }}>{l[1]}: </b>
                            <span style={{ color: '#334155' }}>{l[2]}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* TẢI FILE EXCEL 10 SHEET BANNER */}
                <div className="campaign-download-bar">
                  <div>
                    <h3 style={{ margin: 0, fontSize: 19, fontWeight: 800, color: '#ffffff' }}>
                      Tải Bộ File Excel Kế Hoạch 10 Sheet
                    </h3>
                    <p style={{ margin: '4px 0 0', color: '#d1fae5', fontSize: 14 }}>
                      File Excel tạo tự động gồm: 1.Tổng quan, 2.Chương trình, 3.Kho quà, 4.Nhật ký ngày, 5.Phát quà, 6.Phiếu, 7.Thành viên, 8.Nghiệm thu, 9.Phễu sức khỏe, 10.Giảm chi phí.
                    </p>
                  </div>
                  <Button
                    type="primary"
                    size="large"
                    icon={<DownloadOutlined />}
                    onClick={handleExportExcel}
                    style={{
                      background: '#fde047',
                      borderColor: '#fde047',
                      color: '#0f172a',
                      fontWeight: 800,
                      borderRadius: 10,
                      height: 44
                    }}
                  >
                    Tải File Excel 10 Sheet
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '60px 20px', background: '#ffffff', borderRadius: 16, border: '1px solid #e2e8f0' }}>
              <BulbOutlined style={{ fontSize: 40, color: '#005537', marginBottom: 12 }} />
              <h3 style={{ fontSize: 18, color: '#0f172a' }}>Chưa có phương án</h3>
              <p style={{ color: '#64748b' }}>Vui lòng điền thông tin và bấm "Lên chương trình" để nhận phương án chi tiết.</p>
            </div>
          )}
        </div>
      </div>

      <ToolPurchaseModal open={buyModalOpen} onClose={() => setBuyModalOpen(false)} plans={toolAccess?.requiredPlans || []} />
    </div>
  );
}

export default PharmacySalesCampaignToolPage;
